/**
 * Letter Collection Book.
 *
 * Grid of all letters in the MVP corpus. Mastered letters (any stars)
 * are shown in full colour with their star count. Un-mastered letters
 * show as silhouettes behind a lock icon to motivate.
 *
 * Tapping a mastered letter opens a detail panel that shows all four
 * forms + the learned word cards (image + Arabic + English).
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../lib/api';
import { playUrl, unlockAudio } from '../lib/audio';
import { letters as lettersSig, loadContent } from '../state/content';
import { currentUser } from '../state/user';
import { CollectionSkeleton } from '../components/Skeleton';
import { NavButton } from '../components/NavButton';
import type { LetterSummary } from '../state/content';
import type { Word } from '@huroof/shared';

interface ProgressRow {
  letter_id: number;
  stage: number;
  stars: number;
}

export function CollectionBook() {
  const { route } = useLocation();
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [selected, setSelected] = useState<LetterSummary | null>(null);
  const [selectedWords, setSelectedWords] = useState<Word[] | null>(null);

  useEffect(() => {
    void loadContent();
    unlockAudio();
    if (currentUser.value) {
      void api<{ ok: true; progress: ProgressRow[] }>(
        `/api/progress/${currentUser.value.id}`,
      ).then((r) => setProgress(r.progress));
    }
  }, []);

  const starsByLetter = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of progress) {
      m.set(p.letter_id, (m.get(p.letter_id) ?? 0) + p.stars);
    }
    return m;
  }, [progress]);

  async function openLetter(l: LetterSummary) {
    setSelected(l);
    setSelectedWords(null);
    const r = await api<{ ok: true; words: Word[] }>(
      `/api/letters/${l.id}/words`,
    );
    setSelectedWords(r.words);
    void playUrl(l.sound_url);
  }

  if (selected) {
    return (
      <div class="flex-1 flex flex-col px-4 py-6 pb-10 max-w-md mx-auto w-full">
        <NavButton
          ar="← الكِتَابُ"
          en="Book"
          onClick={() => setSelected(null)}
          class="self-start mb-4"
        />

        <div class="text-center mb-4">
          <p
            class="font-display text-coral select-none"
            style={{
              fontSize: 'clamp(120px, 40vw, 260px)',
              lineHeight: '1.15',
              paddingTop: '0.08em',
            }}
          >
            {selected.letter}
          </p>
          <p class="text-xl font-display">{selected.name_ar}</p>
          <p class="text-xs text-ink/50" dir="ltr">
            {selected.name_en}
          </p>
        </div>

        <div class="grid grid-cols-4 gap-2 mb-6">
          {[
            ['مُنْفَرِدٌ', 'Alone', selected.form_isolated],
            ['بِدَايَةٌ', 'Start', selected.form_beginning],
            ['وَسَطٌ', 'Middle', selected.form_middle],
            ['نِهَايَةٌ', 'End', selected.form_end],
          ].map(([labelAr, labelEn, form]) => (
            <div key={labelAr} class="rounded-2xl bg-white shadow-sm p-2 text-center">
              <p
                class="font-body font-bold text-coral"
                style={{ fontSize: '34px', lineHeight: '1.2', paddingTop: '0.1em' }}
              >
                {form}
              </p>
              <p class="text-[10px] text-ink/50 mt-1">{labelAr}</p>
              <p class="text-[9px] text-ink/40" dir="ltr">{labelEn}</p>
            </div>
          ))}
        </div>

        <h3 class="text-sm font-display text-ink/60 mb-2">
          الكَلِمَاتُ <span class="text-[10px] opacity-60" dir="ltr">— Words</span>
        </h3>

        {!selectedWords && (
          <div class="text-center text-ink/40 py-6">…</div>
        )}
        {selectedWords && (
          <div class="grid grid-cols-2 gap-3">
            {selectedWords.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => void playUrl(w.audio_url)}
                class="rounded-2xl bg-white shadow-sm p-2 text-center active:scale-95"
              >
                <img
                  src={w.image_url}
                  alt={w.meaning_en}
                  class="w-full aspect-square object-cover rounded-xl bg-sand"
                  loading="lazy"
                />
                <p class="mt-2 text-base font-display text-coral">{w.word_ar}</p>
                <p class="text-[10px] text-ink/50" dir="ltr">
                  {w.meaning_en}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const ls = lettersSig.value;

  // No letters fetched yet → content-shaped skeleton placeholder.
  if (!ls) return <CollectionSkeleton />;

  return (
    <div class="flex-1 px-4 py-6 pb-24 max-w-3xl mx-auto w-full">
      <header class="flex items-center justify-between mb-6 px-2">
        <NavButton ar="← الخَرِيطَةُ" en="Map" onClick={() => route('/map')} />
        <div class="text-right">
          <h1 class="text-2xl font-display text-coral leading-none">كِتَابُ الحُرُوفِ</h1>
          <p class="text-[10px] font-body text-ink/50 mt-0.5" dir="ltr">Letter book</p>
        </div>
      </header>

      <div class="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {ls.map((l) => {
          const stars = starsByLetter.get(l.id) ?? 0;
          const mastered = stars > 0;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => mastered && openLetter(l)}
              disabled={!mastered}
              class={`relative aspect-square rounded-3xl grid place-items-center font-display shadow-lg transition-transform
                      ${mastered ? 'bg-white text-coral active:scale-95 hover:scale-[1.02]' : 'bg-ink/15 text-ink/30 cursor-not-allowed'}`}
            >
              <span
                class="font-body font-bold select-none"
                style={{
                  fontSize: 'clamp(52px, 13vw, 100px)',
                  lineHeight: '1.2',
                  paddingTop: '0.1em',
                }}
              >
                {l.letter}
              </span>
              {mastered ? (
                <span class="absolute bottom-1 right-2 text-xs bg-sun text-ink rounded-full px-2 py-0.5">
                  ⭐ {stars}
                </span>
              ) : (
                <span class="absolute top-2 right-2 text-xl" aria-hidden="true">
                  🔒
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
